"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import type { AiProfile } from "@/types/ai";
import { db } from "@/lib/db";
import { generateLocalSummary } from "@/lib/ai/privacy";
import { limitContext } from "@/lib/ai/privacy";
import { getEnabledTools } from "@/lib/tools/registry";
import { trackEvent } from "@/lib/analytics/trackEvent";
import BrandStyles from "@/components/branding/BrandStyles";
import type { ChatMessage } from "@/lib/db/schema";
import ChatSidebar from "./ChatSidebar";
import PremiumSendButton from "./PremiumSendButton";
import ChatMessageComponent from "./ChatMessage";
import ClarityTracker from "@/components/analytics/ClarityTracker";
import { Menu } from "lucide-react";
import { getInputPlaceholder, getEmptyStateMessage, getLoadingMessage } from "@/lib/utils/microcopy";
import { loadKarmaProfile } from "@/lib/karma/profileManager";
import { getTextColorForBackground, getSecondaryTextColorForBackground, getTextColorWithOpacity } from "@/lib/utils/colorContrast";
import RingSystem from "@/components/ui/RingSystem";
import ResponseModeSelector from "./ResponseModeSelector";
import UltraNavigationBar from "./UltraNavigationBar";
import type { ResponseMode } from "@/lib/ai/responseModes";
import { RESPONSE_MODES, detectBestMode } from "@/lib/ai/responseModes";
import { getAtlas } from "@/lib/atlas/integration";
import type { AtlasSearchProcess as AtlasProcess } from "@/lib/atlas/integration";
import { useAtlasGhostStream } from "@/lib/atlas/useAtlasGhostStream";
import AtlasProcessingOverlay from "@/components/atlas/processing/AtlasProcessingOverlay";
import { getUserPlan } from "@/lib/subscription/check";
import { getPlanFeatures } from "@/lib/subscription/plans";
import { getSearchFunctions } from "@/lib/chat/getSearchFunctions";

interface Props {
  profile: AiProfile;
  initialPrompt?: string;
  chatWindowId?: string; // For multi-chat coordination
  connectedChatIds?: string[]; // IDs of connected chat windows
  role?: { id: string; name: string; role: string; description: string; turnOrder: number }; // AI role
  onMessageSent?: (message: string) => void; // Callback when message is sent
  onMessageComplete?: () => void; // Callback when AI response completes
  hasMultiChat?: boolean; // Whether user has purchased multi-chat neuroblock
  hideSidebar?: boolean; // Hide sidebar (for preview mode)
}

export default function ChatInterface({ 
  profile, 
  initialPrompt,
  chatWindowId,
  connectedChatIds = [],
  role,
  onMessageSent,
  onMessageComplete,
  hasMultiChat = false,
  hideSidebar = false,
}: Props) {
  const router = useRouter();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState(initialPrompt || "");
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [typingMessage, setTypingMessage] = useState<{ id: string; content: string; fullContent: string } | null>(null);
  const [karmaProfile, setKarmaProfile] = useState<any>(null);
  const [responseMode, setResponseMode] = useState<ResponseMode>("auto");
  // Atlas is always active - persistent per conversation
  const [atlasProcess, setAtlasProcess] = useState<AtlasProcess | null>(null);
  const [atlasSkipped, setAtlasSkipped] = useState(false);
  const [atlasNavigationResult, setAtlasNavigationResult] = useState<any>(null);
  // Persistent Atlas history - builds the cognitive map across all messages in this conversation
  const [atlasHistory, setAtlasHistory] = useState<Array<{
    process: AtlasProcess;
    result: any;
    messageId: string;
    timestamp: number;
  }>>([]);
  /** Image attachments for vision input (upload image + question) */
  const [attachments, setAttachments] = useState<Array<{ type: "image"; base64: string; mimeType?: string }>>([]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const lastFailedUserMessageRef = useRef<string>("");
  const pendingBubbleRef = useRef<HTMLDivElement>(null);
  const streamingBubbleRef = useRef<HTMLDivElement>(null);
  const lastSentMessageLengthRef = useRef<number>(0);
  const [collapseInProgress, setCollapseInProgress] = useState(false);
  const [showGlyphShimmer, setShowGlyphShimmer] = useState(false);
  const prevContentLengthRef = useRef(0);
  const sendMessageRef = useRef<(messageContent: string, skipTurnCheck?: boolean) => Promise<void>>(() => Promise.resolve());

  // Initialize Atlas on mount - always active from first message
  useEffect(() => {
    if (typeof window !== "undefined") {
      const atlas = getAtlas();
      atlas.initialize().then(() => {
        // Initialize with a default "ready" state
        setAtlasProcess({
          status: 'ready',
          message: 'Atlas cognitive map ready',
          currentLocation: undefined,
          nearbyAreas: undefined,
        });
      });
    }
  }, []);
  
  // Reset Atlas when conversation changes (new chatWindowId)
  useEffect(() => {
    if (chatWindowId) {
      // Load conversation-specific Atlas history from localStorage
      const savedHistory = localStorage.getItem(`atlas_history_${chatWindowId}`);
      if (savedHistory) {
        try {
          const parsed = JSON.parse(savedHistory);
          setAtlasHistory(parsed.history || []);
          if (parsed.lastResult) {
            setAtlasNavigationResult(parsed.lastResult);
            setAtlasProcess({
              status: 'ready',
              message: `Continuing in ${parsed.lastResult.location?.topicName || 'cognitive space'}`,
              currentLocation: parsed.lastResult.location?.topicName,
              nearbyAreas: parsed.lastResult.orientation?.nearbyTopics?.map((t: any) => t.name) || [],
            });
          }
        } catch (e) {
          console.error("Failed to load Atlas history:", e);
        }
      } else {
        // New conversation - reset Atlas
        setAtlasHistory([]);
        setAtlasNavigationResult(null);
        setAtlasProcess({
          status: 'ready',
          message: 'Atlas cognitive map ready',
          currentLocation: undefined,
          nearbyAreas: undefined,
        });
      }
    }
  }, [chatWindowId]);
  
  // Save Atlas history to localStorage when it changes
  useEffect(() => {
    if (chatWindowId && atlasHistory.length > 0) {
      localStorage.setItem(`atlas_history_${chatWindowId}`, JSON.stringify({
        history: atlasHistory,
        lastResult: atlasNavigationResult,
      }));
    }
  }, [atlasHistory, atlasNavigationResult, chatWindowId]);
  
  // Sidebar toggle - user can hide/show on desktop too
  useEffect(() => {
    // Check if user has a preference saved
    const saved = localStorage.getItem("sidebar_open");
    if (saved !== null) {
      setSidebarOpen(saved === "true");
    } else {
      // Default: open on desktop, closed on mobile
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
        } else {
          setSidebarOpen(false);
      }
    };
    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    }
  }, []);
  
  // Save sidebar state when it changes
  useEffect(() => {
    localStorage.setItem("sidebar_open", String(sidebarOpen));
  }, [sidebarOpen]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messageQueueRef = useRef<Map<string, string>>(new Map()); // For queued messages

  useEffect(() => {
    if (initialPrompt && inputRef.current) {
      inputRef.current.focus();
    }
  }, [initialPrompt]);

  useEffect(() => {
    loadMessages();
  }, [profile.id]);
  
  // Load KARMA profile for microcopy
  useEffect(() => {
    async function loadKarma() {
      try {
        const karma = await loadKarmaProfile(profile.id);
        setKarmaProfile(karma);
      } catch (error) {
        console.error("Failed to load KARMA profile:", error);
      }
    }
    loadKarma();
  }, [profile.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, typingMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // When user returns to the window/tab, scroll back to bottom so messages aren't "stacked at top"
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && messages.length > 0) {
        scrollToBottom();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [messages.length]);

  async function loadMessages() {
    try {
      if (typeof window === "undefined") {
        return;
      }
      if (!db || !db.chatMessages) {
        console.warn("Database not initialized yet");
        return;
      }
      const msgs = await db.chatMessages
        .where("profileId")
        .equals(profile.id)
        .sortBy("createdAt");
      // Keep chronological order (oldest first) for proper conversation flow
      setMessages(msgs);
    } catch (error) {
      console.error("Failed to load messages:", error);
      setMessages([]);
    }
  }

  // Listen for turn grants (when queued message can be sent)
  useEffect(() => {
    if (!chatWindowId) return;

    const handleTurnGranted = async (evt: Event) => {
      const { detail } = evt as CustomEvent<{ chatId: string; message: string }>;
      if (detail.chatId === chatWindowId) {
        const queuedMessage = messageQueueRef.current.get(chatWindowId);
        if (queuedMessage && !loading) {
          messageQueueRef.current.delete(chatWindowId);
          // Send the queued message (skip turn check since turn is already granted)
          await sendMessageRef.current(queuedMessage, true);
        }
      }
    };

    window.addEventListener("multiChatTurnGranted", handleTurnGranted);
    return () => {
      window.removeEventListener("multiChatTurnGranted", handleTurnGranted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatWindowId, loading]); // sendMessage is stable (defined in component)

  // Listen for "Send to AI" from overview panel (multi-chat)
  useEffect(() => {
    if (!chatWindowId) return;
    const handleSendFromOverview = (evt: Event) => {
      const { chatId, message } = (evt as CustomEvent<{ chatId: string; message: string }>).detail || {};
      if (chatId === chatWindowId && message && !loading) {
        sendMessageRef.current(message, true);
      }
    };
    window.addEventListener("multiChatSendFromOverview", handleSendFromOverview);
    return () => window.removeEventListener("multiChatSendFromOverview", handleSendFromOverview);
  }, [chatWindowId, loading]); // sendMessage is stable

  async function sendMessage(messageContent: string, skipTurnCheck = false) {
    if (!messageContent.trim() || loading) return;

    // Check turn-taking for connected chats BEFORE sending (unless turn already granted)
    if (!skipTurnCheck && chatWindowId && connectedChatIds.length > 0) {
      const { multiChatCoordinator } = await import("@/lib/chat/multiChatCoordinator");
      const canSpeak = multiChatCoordinator.canSpeak(chatWindowId, [chatWindowId, ...connectedChatIds]);
      
      if (!canSpeak.canSpeak) {
        const { eventBus } = await import("@/lib/utils/eventBus");
        eventBus.emit("TOAST:SHOW", {
          message: canSpeak.reason || "Please wait for your turn",
          type: "info",
        });
        return;
      }

      // Request turn
      const turnResult = await multiChatCoordinator.requestTurn(
        chatWindowId,
        [chatWindowId, ...connectedChatIds],
        messageContent
      );

      if (!turnResult.granted) {
        // Message queued - save user message and wait for turn
        const userMessage: ChatMessage = {
          id: crypto.randomUUID(),
          profileId: profile.id,
          role: "user",
          content: messageContent,
          createdAt: Date.now(),
        };
        try {
          if (typeof window !== "undefined" && db && db.chatMessages) {
            await db.chatMessages.add(userMessage);
          }
        } catch (error) {
          console.error("Failed to save message:", error);
        }
        setMessages((prev) => [...prev, userMessage]);
        
        // Store message for when turn is granted
        messageQueueRef.current.set(chatWindowId, messageContent);
        
        // Notify parent about queue position
        if (onMessageSent) {
          onMessageSent(messageContent);
        }
        return;
      }
    }

    // Turn granted or not in multi-chat mode - proceed with sending
    const displayContent = messageContent || (attachments.length > 0 ? "Attached image(s)" : "");
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      profileId: profile.id,
      role: "user",
      content: displayContent,
      createdAt: Date.now(),
    };

    // Save user message
    try {
      if (typeof window !== "undefined" && db && db.chatMessages) {
        await db.chatMessages.add(userMessage);
      }
    } catch (error) {
      console.error("Failed to save message:", error);
      // Continue even if save fails
    }
    setMessages((prev) => [...prev, userMessage]);
    lastSentMessageLengthRef.current = messageContent.length;
    // Input already cleared in handleSend, don't clear again
    setLoading(true);

    // Notify coordinator that message is being sent (for turn tracking)
    if (onMessageSent && chatWindowId) {
      onMessageSent(messageContent);
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      // Get response mode config
      const modeConfig = RESPONSE_MODES[responseMode];
      const userPlan = getUserPlan(profile);
      const planFeatures = getPlanFeatures(userPlan);
      
      // ALWAYS use Atlas for cognitive navigation (for now, to test)
      // In production, this would be: const useAtlas = modeConfig.useAtlas && !atlasSkipped;
      const useAtlas = !atlasSkipped; // Always use Atlas to show thinking process
      let localAtlasNavigationResult = null;
      
      console.log("🧭 Atlas check - useAtlas:", useAtlas, "atlasSkipped:", atlasSkipped, "window:", typeof window);
      
      // Run Atlas navigation
      if (useAtlas && typeof window !== "undefined") {
        try {
          setAtlasSkipped(false);
          const atlas = getAtlas();
          
          console.log("🧭 Starting Atlas navigation for:", messageContent);
          
          // Show Atlas search process
          const process = await atlas.getSearchProcess(messageContent, (process) => {
            console.log("🧭 Atlas process update:", process.status, process.message);
            setAtlasProcess(process);
          });
          
          console.log("🧭 Atlas process complete:", process.status, process);
          
          if (process.status !== 'skipped') {
            // Build context from previous navigations (building the map)
            const previousNavigations = atlasHistory.map(entry => entry.result?.location?.topicName || '').filter(Boolean);
            const currentTopic = atlasHistory.length > 0 
              ? atlasHistory[atlasHistory.length - 1].result?.location?.topicName 
              : undefined;
            
            // Navigate using Atlas - with context from previous navigations
            localAtlasNavigationResult = await atlas.navigate(messageContent, {
              currentTopic: currentTopic,
              previousNavigations: previousNavigations,
            });
            console.log("🧭 Atlas navigation result:", localAtlasNavigationResult);
            setAtlasNavigationResult(localAtlasNavigationResult);
            // Keep Atlas always active - update to "ready" state (never "complete" - always building)
            setAtlasProcess({
              ...process,
              status: 'ready', // Always ready for next navigation
              message: `Navigated to ${localAtlasNavigationResult.location?.topicName || 'cognitive space'}`,
              currentLocation: localAtlasNavigationResult.location?.topicName,
              nearbyAreas: localAtlasNavigationResult.orientation?.nearbyTopics?.map((t: any) => t.name) || [],
            });
            
            // Add to persistent history - building the cognitive map (use user message id so cards match)
            const historyEntry = {
              process: process,
              result: localAtlasNavigationResult,
              messageId: userMessage.id,
              timestamp: Date.now(),
            };
            setAtlasHistory(prev => {
              const updated = [...prev, historyEntry];
              console.log("🧭 Atlas history updated, total entries:", updated.length);
              return updated;
            });
            console.log("🧭 Atlas state set - process:", process, "result:", localAtlasNavigationResult);
          } else {
            console.log("🧭 Atlas skipped but keeping active");
            // Keep Atlas active even when skipped
            setAtlasProcess({
              status: 'ready',
              message: 'Atlas cognitive map ready',
              currentLocation: atlasNavigationResult?.location?.topicName,
              nearbyAreas: atlasNavigationResult?.orientation?.nearbyTopics?.map((t: any) => t.name) || [],
            });
          }
        } catch (error) {
          console.error("❌ Atlas navigation error:", error);
          // Keep Atlas active even on error - just update status
          setAtlasProcess({
            status: 'ready',
            message: 'Atlas ready - navigation encountered an issue',
            currentLocation: atlasNavigationResult?.location?.topicName,
            nearbyAreas: atlasNavigationResult?.orientation?.nearbyTopics?.map((t: any) => t.name) || [],
          });
        }
      } else {
        console.log("🧭 Atlas not used but keeping active");
        // Keep Atlas active even when not used
        setAtlasProcess({
          status: 'ready',
          message: 'Atlas cognitive map ready',
          currentLocation: atlasNavigationResult?.location?.topicName,
          nearbyAreas: atlasNavigationResult?.orientation?.nearbyTopics?.map((t: any) => t.name) || [],
        });
      }

      // Retrieve context from vault if available
      let retrievedContext: {
        chunks?: Array<{ text: string; source: string }>;
        summary?: string;
        atlasNavigation?: any; // Atlas navigation result
      } | undefined;

      let vaultFiles = [];
      try {
        if (typeof window !== "undefined" && db && db.vaultFiles) {
          vaultFiles = await db.vaultFiles
            .where("profileId")
            .equals(profile.id)
            .toArray();
        }
      } catch (error) {
        console.error("Failed to load vault files:", error);
        // Continue without vault files
      }

      if (vaultFiles.length > 0) {
        // Dynamically load search functions to prevent sharp from loading on server
        const { searchChunks } = await getSearchFunctions();
        const searchResults = await searchChunks(profile.id, messageContent.trim(), 8);

        if (searchResults && searchResults.length > 0) {
          if (profile.privacyMode === "strict") {
            // Generate local summary
            const chunks = searchResults.map((r) => r.chunk);
            const summary = generateLocalSummary(chunks);
            retrievedContext = { 
              summary,
              atlasNavigation: localAtlasNavigationResult,
            };
          } else {
            // Smart mode: send excerpts; when many chunks, add doc overview for synthesis
            const { getChunkSource } = await getSearchFunctions();
            const chunksWithSource = await Promise.all(
              searchResults.map(async (r) => ({
                text: r.chunk.text,
                source: await getChunkSource(r.chunk),
              }))
            );
            const limitedChunks = limitContext(chunksWithSource, 2500);
            const docOverview =
              searchResults.length >= 5
                ? generateLocalSummary(searchResults.map((r) => r.chunk), 5)
                : undefined;
            retrievedContext = {
              chunks: limitedChunks,
              ...(docOverview && { summary: docOverview }),
            };
          }
        }
        
                // Add Atlas navigation if available
                if (localAtlasNavigationResult) {
                  retrievedContext = {
                    ...retrievedContext,
                    atlasNavigation: localAtlasNavigationResult,
                  };
                }
      } else {
        // No vault files - use Atlas if available
        if (localAtlasNavigationResult) {
          // Convert Atlas landmarks to context chunks
          if (localAtlasNavigationResult.success && localAtlasNavigationResult.location?.landmarks) {
            const landmarks = localAtlasNavigationResult.location.landmarks;
            retrievedContext = {
              chunks: landmarks.map(landmark => ({
                text: `${landmark.title}\n${landmark.summary}\n${landmark.structuredFacts.join('\n')}`,
                source: landmark.url || landmark.id,
              })),
              atlasNavigation: localAtlasNavigationResult,
            };
          } else {
            retrievedContext = {
              atlasNavigation: localAtlasNavigationResult,
            };
          }
        }
      }

      // Get enabled tools (registry: create_task, draft_email, etc.)
      const enabledTools = await getEnabledTools(profile.agentModeEnabled);

      // Connected integrations (Gmail, Outlook, Stripe, etc.) so the API can add action tools without affecting other logic
      let connectedIntegrationTypes: string[] | undefined;
      try {
        if (db?.integrations) {
          const enabled = await db.integrations.where("enabled").equals(1).toArray();
          connectedIntegrationTypes = enabled.map((i) => i.type);
        }
      } catch {
        // ignore
      }

      // Call Claude API with conversation history
      const conversationHistory = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      // Get user ID for tracking
      const { getUserId } = await import("@/lib/user/identity");
      const userId = getUserId();
      
      // Enhanced fetch for mobile compatibility
      const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log(`[ChatInterface] Sending request - Mobile: ${isMobile}, User-Agent: ${navigator.userAgent.substring(0, 50)}`);
      
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const timeoutMs = isMobile ? 120000 : 60000;
      timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      // Power-up: fetch persistent memory context when capabilities.memory is enabled
      let memoryContext: string | undefined;
      if (profile?.capabilities?.memory?.enabled && profile?.id) {
        try {
          const memRes = await fetch(
            `/api/assistant-memory/context?userId=${encodeURIComponent(userId)}&aiId=${encodeURIComponent(profile.id)}`
          );
          if (memRes.ok) {
            const memData = await memRes.json();
            memoryContext = memData.context;
          }
        } catch {
          // ignore
        }
      }

      const response = await fetch("/api/claude", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "text/event-stream, application/json", // Accept SSE or JSON
        },
        signal: controller.signal,
        body: JSON.stringify({
          profile,
          message: messageContent,
          conversationHistory,
          retrievedContext,
          enabledTools,
          connectedIntegrationTypes, // Gmail, Outlook, Stripe, etc. – used to add action tools (send email, invoice)
          userId,
          responseMode,
          stream: true,
          modeConfig: {
            thinkingDepth: modeConfig.thinkingDepth,
            maxTokens: modeConfig.maxTokens,
            temperature: modeConfig.temperature,
          },
          attachments: attachments.length > 0 ? attachments : undefined,
          ...(memoryContext && { memoryContext }),
        }),
      });

      // Check if response is streaming (SSE)
      const contentType = response.headers.get("content-type") || "";
      const isStreaming = contentType.includes("text/event-stream");

      if (isStreaming) {
        // Handle SSE streaming
        const messageId = crypto.randomUUID();
        let accumulatedContent = "";
        let finalUsage: { inputTokens: number; outputTokens: number } | undefined;
        
        // Show typing indicator immediately
        setTypingMessage({ id: messageId, content: "", fullContent: "" });

        if (!response.body) {
          throw new Error("No response body for streaming");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // Keep incomplete line

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                const dataStr = line.slice(6).trim();
                if (dataStr === "[DONE]" || dataStr === "") continue;

                try {
                  const data = JSON.parse(dataStr);
                  
                  if (data.done) {
                    // Stream complete
                    finalUsage = data.usage;
                    accumulatedContent = accumulatedContent.trim();
                    
                    // Save final message
                    const assistantMessage: ChatMessage = {
                      id: messageId,
                      profileId: profile.id,
                      role: "assistant",
                      content: accumulatedContent,
                      createdAt: Date.now(),
                    };

                    try {
                      if (typeof window !== "undefined" && db && db.chatMessages) {
                        await db.chatMessages.add(assistantMessage);
                      }
                    } catch (error) {
                      console.error("Failed to save message:", error);
                    }

                    setMessages((prev) => [...prev, assistantMessage]);
                    setTypingMessage(null);
                    setLoading(false);
                    
                    if (onMessageComplete) {
                      onMessageComplete();
                    }
                    return;
                  } else if (data.text) {
                    // Append token
                    accumulatedContent += data.text;
                    setTypingMessage({ 
                      id: messageId, 
                      content: accumulatedContent,
                      fullContent: accumulatedContent,
                    });
                  } else if (data.error) {
                    // Don't throw – show error in chat and exit stream so we don't crash the error boundary
                    const raw = typeof data.error === "string" ? data.error : JSON.stringify(data.error);
                    const isModelNotFound = /404|not_found|model.*not found/i.test(raw);
                    const errorText = isModelNotFound
                      ? "The AI model could not be found. Please check your API configuration."
                      : "Something went wrong. Please try again.";
                    const errorMessage: ChatMessage & { isError?: boolean } = {
                      id: crypto.randomUUID(),
                      profileId: profile.id,
                      role: "assistant",
                      content: errorText,
                      createdAt: Date.now(),
                      isError: true,
                    };
                    setMessages((prev) => [...prev, errorMessage]);
                    setTypingMessage(null);
                    setLoading(false);
                    setAtlasProcess(null);
                    setAtlasNavigationResult(null);
                    if (onMessageComplete && chatWindowId && connectedChatIds?.length) onMessageComplete();
                    return;
                  }
                } catch (parseError) {
                  // Skip invalid JSON (might be empty lines)
                  if (dataStr !== "") {
                    console.warn("Failed to parse SSE data:", dataStr);
                  }
                }
              }
            }
          }
        } catch (streamError: any) {
          console.error("Streaming error:", streamError);
          // If stream aborted (e.g. user opened Atlas, tab backgrounded), still return whatever we got so it appears in chat
          const partial = accumulatedContent.trim();
          if (partial.length > 0) {
            const assistantMessage: ChatMessage = {
              id: messageId,
              profileId: profile.id,
              role: "assistant",
              content: partial,
              createdAt: Date.now(),
            };
            try {
              if (typeof window !== "undefined" && db && db.chatMessages) {
                await db.chatMessages.add(assistantMessage);
              }
            } catch (e) {
              console.error("Failed to save partial message:", e);
            }
            setMessages((prev) => [...prev, assistantMessage]);
            if (onMessageComplete) onMessageComplete();
          }
          setTypingMessage(null);
          setLoading(false);
          if (partial.length === 0) throw streamError;
        }
        return; // Exit early for streaming
      }

      if (!response.ok) {
        // Try to get error data, but handle non-JSON responses
        let errorData: any = {};
        let errorText = "";
        
        try {
          const contentType = response.headers.get("content-type");
          if (contentType?.includes("application/json")) {
            errorData = await response.json();
          } else {
            // Not JSON, try to read as text
            errorText = await response.text();
            console.error("❌ Non-JSON error response:", errorText);
            // Try to parse as JSON anyway (sometimes it's JSON but wrong content-type)
            try {
              errorData = JSON.parse(errorText);
            } catch {
              // Not JSON, use text as error message
              errorData = { error: errorText || `HTTP ${response.status}: ${response.statusText}` };
            }
          }
        } catch (parseError) {
          console.error("❌ Failed to parse error response:", parseError);
          errorData = { error: "Request failed." };
        }
        
        // User-facing message only — never show status codes, "API error", or "check console"
        const friendlyFallback = "Something went wrong. Please try again.";
        let errorMessage = friendlyFallback;
        
        if (errorData.details) {
          if (typeof errorData.details === "string") {
            errorMessage = errorData.details;
          } else if (errorData.details.originalError) {
            errorMessage = errorData.details.originalError;
          } else if (errorData.details.message) {
            errorMessage = errorData.details.message;
          }
        }
        if (errorMessage === friendlyFallback && errorData.error) {
          if (typeof errorData.error === "string") {
            errorMessage = errorData.error;
          } else if (errorData.error.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.type === "not_found_error" || errorData.error?.type === "not_found_error") {
            errorMessage = "The AI model could not be found. Please check your API configuration.";
          }
        }
        if (errorMessage === friendlyFallback && errorData.content && typeof errorData.content === "string") {
          errorMessage = errorData.content;
        }
        if (errorData.suggestion) {
          errorMessage = errorMessage === friendlyFallback ? errorData.suggestion : `${errorMessage} ${errorData.suggestion}`;
        }
        // Never expose raw server output; allow structured API messages (e.g. video generation failure with tips)
        const isStructuredError = /Video Generation|⚠️\s*\*\*|API key|environment variables|GOOGLE_GEMINI|XAI_API|OPENAI_API/i.test(errorMessage);
        const isRaw = !isStructuredError && /API error|500|501|502|503|Check browser console|statusText|HTTP \d{3}/i.test(errorMessage);
        if (isRaw || (errorMessage === friendlyFallback && isRaw)) {
          errorMessage = friendlyFallback;
        }
        
        console.error("❌ Chat API error:", {
          status: response.status,
          statusText: response.statusText,
          errorData,
          errorText,
          url: response.url,
          finalErrorMessage: errorMessage,
          errorDataKeys: Object.keys(errorData),
          errorDataString: JSON.stringify(errorData, null, 2),
          headers: Object.fromEntries(response.headers.entries()),
        });
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      console.log("✅ Chat API response:", {
        hasContent: !!data.content,
        hasMessage: !!data.message,
        hasImages: !!data.images,
        hasVideos: !!data.videos,
        hasText: !!data.text,
        hasImage: !!data.image,
        hasVideo: !!data.video,
        hasOutputs: !!data.outputs,
        hasErrors: !!data.errors,
        dataKeys: Object.keys(data),
        contentPreview: data.content?.substring(0, 100),
        imagesCount: data.images?.length || 0,
        videosCount: data.videos?.length || 0,
        imageUrl: data.image?.url,
        videoUrl: data.video?.url,
        errors: data.errors,
      });

      // Non-streaming response (fallback)
      const messageId = crypto.randomUUID();
      // API returns 'content', not 'message'
      // Also check for multimodal outputs (text, image, video)
      let fullContent = data.content || data.text?.content || data.message || "";
      
      // Extract images and videos from response
      // Multimodal API spreads outputs, so structure is: { content, text: {...}, image: {...}, video: {...} }
      // Regular API returns: images[], videos[]
      let images: Array<{ url: string; prompt?: string }> = [];
      let videos: Array<{ url?: string; prompt?: string; operationName?: string; status?: string }> = [];
      
      // Check for images array first
      if (data.images && Array.isArray(data.images)) {
        images = data.images.map((img: any) => ({
          url: img.url,
          prompt: img.prompt || img.prompt,
        }));
      } 
      // Check for single image object (from multimodal outputs)
      else if (data.image) {
        if (data.image.url) {
          images = [{
            url: data.image.url,
            prompt: data.image.prompt || data.image.metadata?.prompt,
          }];
        } else {
          console.warn("⚠️ Image object exists but has no URL:", data.image);
        }
      }
      // Check for outputs.image (if not spread)
      else if (data.outputs?.image) {
        if (data.outputs.image.url) {
          images = [{
            url: data.outputs.image.url,
            prompt: data.outputs.image.prompt,
          }];
        } else {
          console.warn("⚠️ outputs.image exists but has no URL:", data.outputs.image);
        }
      }
      
      // Check for videos array first
      if (data.videos && Array.isArray(data.videos)) {
        videos = data.videos;
      }
      // Check for single video object (from multimodal outputs)
      else if (data.video) {
        videos = [data.video];
      }
      // Check for outputs.video (if not spread)
      else if (data.outputs?.video) {
        videos = [data.outputs.video];
      }
      
      // If there are errors, append them to the content so the user sees them
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const errorMessages = data.errors.map((err: any) => {
          if (err.type === "image") {
            return `\n\n⚠️ **Image Generation Failed**: ${err.error}`;
          } else if (err.type === "video") {
            return `\n\n⚠️ **Video Generation Failed**: ${err.error}`;
          } else if (err.type === "text") {
            return `\n\n⚠️ **Text Generation Failed**: ${err.error}`;
          }
          return `\n\n⚠️ **${err.type} Failed**: ${err.error}`;
        });
        fullContent += errorMessages.join("");
      }
      
      console.log("📸 Extracted media:", { 
        imagesCount: images.length, 
        videosCount: videos.length, 
        dataKeys: Object.keys(data),
        imageUrls: images.map(i => i.url),
        videoUrls: videos.map(v => v.url),
        hasErrors: !!(data.errors && data.errors.length > 0),
        hasLocations: !!(data.locations && data.locations.length > 0),
      });
      
      // Extract locations and directions from API response (for location-based queries)
      const locations = data.locations || [];
      const directions = data.directions;
      
      if (locations.length > 0 || directions) {
        console.log("📍 Location data found in API response:", {
          locations: locations.length,
          hasDirections: !!directions,
        });
        // Add locations and directions to Atlas navigation result if it exists
        if (localAtlasNavigationResult) {
          if (locations.length > 0) {
            localAtlasNavigationResult.locations = locations;
          }
          if (directions) {
            localAtlasNavigationResult.directions = directions;
          }
          setAtlasNavigationResult(localAtlasNavigationResult);
        }
      }
      
      if (!fullContent && images.length === 0 && videos.length === 0) {
        console.error("❌ No content, images, or videos in API response:", data);
        throw new Error("No content received from AI. Response: " + JSON.stringify(data));
      }
      
      // Start typing effect
      setTypingMessage({ id: messageId, content: "", fullContent });
      
      // Type out the message character by character
      let currentIndex = 0;
      const typingInterval = setInterval(() => {
        currentIndex += 1;
        // Safety check - ensure fullContent is a string
        if (!fullContent || typeof fullContent !== 'string') {
          console.error("❌ Invalid fullContent in typing effect:", fullContent);
          clearInterval(typingInterval);
          setLoading(false);
          return;
        }
        const partialContent = fullContent.slice(0, currentIndex);
        setTypingMessage({ id: messageId, content: partialContent, fullContent });
        
        if (currentIndex >= fullContent.length) {
          clearInterval(typingInterval);
          
          // Save complete message with images and videos
          const assistantMessage: ChatMessage = {
            id: messageId,
            profileId: profile.id,
            role: "assistant",
            content: fullContent,
            createdAt: Date.now(),
            images: images.length > 0 ? images.map((img: any) => ({
              url: typeof img === 'string' ? img : (img.url || img),
              prompt: img.prompt,
            })) : undefined,
            videos: videos.length > 0 ? videos.map((vid: any) => ({
              url: vid.url,
              prompt: vid.prompt,
              operationName: vid.operationName,
              status: vid.status,
            })) : undefined,
          };
          
          console.log("💾 Saving message with media:", {
            hasImages: !!assistantMessage.images,
            imagesCount: assistantMessage.images?.length || 0,
            hasVideos: !!assistantMessage.videos,
            videosCount: assistantMessage.videos?.length || 0,
          });

          try {
            if (typeof window !== "undefined" && db && db.chatMessages) {
              db.chatMessages.add(assistantMessage);
            }
          } catch (error) {
            console.error("Failed to save assistant message:", error);
          }
          
          setMessages((prev) => [...prev, assistantMessage]);
          setTypingMessage(null);
              setLoading(false);
              
              // Keep Atlas process visible for a bit after message completes
              // so user can review the thinking process
              setTimeout(() => {
                // Don't clear immediately - let user see the thought stream
              }, 5000);
              
              // Notify coordinator that message is complete (for turn-taking)
              if (onMessageComplete && chatWindowId) {
                onMessageComplete();
              }
            }
          }, 15); // Adjust speed: lower = faster typing
      
      // Track analytics
      await trackEvent(profile.id, "message", {
        messageLength: messageContent.length,
        hasContext: !!(retrievedContext && (retrievedContext.chunks?.length || retrievedContext.summary)),
      });
            } catch (error: any) {
              console.error("❌ Chat error caught:", error);
              console.error("❌ Chat error message:", error?.message);
              console.error("❌ Chat error type:", typeof error);
              console.error("❌ Chat error keys:", Object.keys(error || {}));
              console.error("❌ Chat error full:", JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
              
              lastFailedUserMessageRef.current = messageContent;

              let errorText = "";
              if (error?.name === "AbortError") {
                errorText = "Request was stopped.";
              } else {
              const errorMsg = error?.message || error?.error || error?.details || String(error);
              const isDev = typeof window !== "undefined" && (process.env.NODE_ENV === "development" || /localhost|127\.0\.0\.1/.test(window.location.hostname));
              const debugHint = isDev ? " Visit /api/debug/env to check your configuration." : " Please check your environment variables or contact support.";
              if (errorMsg?.includes("No AI provider") || errorMsg?.includes("not configured") || errorMsg?.includes("AI service is not configured")) {
                errorText = "AI service is not configured. Please configure at least one API key in your environment variables." + debugHint;
              } else if (errorMsg?.includes("404") || errorMsg?.includes("not_found") || (errorMsg?.includes("model") && errorMsg?.includes("not found"))) {
                errorText = "The AI model could not be found. Please check your API configuration." + debugHint;
              } else if (errorMsg?.includes("401") || errorMsg?.includes("403") || errorMsg?.includes("API key") || errorMsg?.includes("authentication") || errorMsg?.includes("authentication failed")) {
                errorText = "API authentication failed. Please check your API keys." + debugHint;
              } else if (errorMsg?.includes("429") || errorMsg?.includes("rate limit")) {
                errorText = "Rate limit exceeded. Please wait a moment and try again.";
              } else if (errorMsg?.includes("All available providers failed")) {
                errorText = "All AI providers failed. This usually means API keys are missing or invalid." + debugHint;
              } else if (errorMsg?.includes("Video generation") || errorMsg?.includes("video generation")) {
                errorText = errorMsg.includes("API key") || errorMsg.includes("GOOGLE_GEMINI") || errorMsg.includes("XAI_API") || errorMsg.includes("OPENAI_API")
                  ? errorMsg
                  : "Video generation isn't available right now. You need at least one of: GOOGLE_GEMINI_API_KEY (Veo), XAI_API_KEY (Grok Imagine), or OPENAI_API_KEY (Sora) with video access." + debugHint;
              } else if (errorMsg) {
                errorText = typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg);
              } else {
                errorText = "Something went wrong. Please try again." + debugHint;
              }
              }
              
              const errorMessage: ChatMessage & { isError?: boolean } = {
                id: crypto.randomUUID(),
                profileId: profile.id,
                role: "assistant",
                content: errorText,
                createdAt: Date.now(),
                isError: true,
              };
              try {
                if (typeof window !== "undefined" && db && db.chatMessages) {
                  await db.chatMessages.add({ ...errorMessage, isError: undefined });
                }
              } catch (dbError) {
                console.error("Failed to save error message:", dbError);
              }
              setMessages((prev) => [...prev, errorMessage]);
              
              // Clear Atlas on error
              setAtlasProcess(null);
              setAtlasNavigationResult(null);
              
              // Release turn on error
              if (onMessageComplete && chatWindowId && connectedChatIds.length > 0) {
                onMessageComplete();
              }
            } finally {
              if (timeoutId !== undefined) clearTimeout(timeoutId);
              abortControllerRef.current = null;
              setLoading(false);
            }
  }
  sendMessageRef.current = sendMessage;

  // Get gradient colors based on brand - ensure we use the actual profile colors
  const getGradientColors = () => {
    // Always use brand colors from profile - no fallbacks that might override custom colors
    const primary = profile.brand?.primaryColor || "#87CEEB";
    const secondary = profile.brand?.secondaryColor || "#B0E0E6";
    return { primary, secondary };
  };

  const gradientColors = getGradientColors();
  const ghostStreamItems = useAtlasGhostStream(atlasProcess, atlasHistory, 6);

  const prevLoadingRef = useRef(loading);
  useEffect(() => {
    if (prevLoadingRef.current === true && loading === false) setCollapseInProgress(true);
    prevLoadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    const len = typingMessage?.content?.length ?? 0;
    if (len > 0 && prevContentLengthRef.current === 0) {
      setShowGlyphShimmer(true);
      const t = setTimeout(() => setShowGlyphShimmer(false), 160);
      return () => clearTimeout(t);
    }
    prevContentLengthRef.current = len;
  }, [typingMessage?.content?.length]);

  // Derive bubble styles from profile.brand.bubbleConfig or profile.customTheme
  const bubbleConfig = profile.brand.bubbleConfig || {};
  const userBubbleRadius = bubbleConfig.userBubbleRadius || profile.customTheme?.bubbles?.userBubbleRadius || "1rem";
  const assistantBubbleRadius = bubbleConfig.assistantBubbleRadius || profile.customTheme?.bubbles?.assistantBubbleRadius || "1rem";
  const enableShadows = bubbleConfig.enableShadows !== false && profile.customTheme?.bubbles?.assistantBubbleShadow !== false;
  const bubblePadding = bubbleConfig.bubblePadding || profile.customTheme?.bubbles?.bubblePadding || "1.25rem";

  // Calculate theme-aware text colors for input area (white glass background)
  // White glass is typically rgba(255, 255, 255, 0.8), so we use white as base
  const inputBackgroundColor = "#ffffff"; // Base white for glass effect
  const inputTextColor = getTextColorForBackground(inputBackgroundColor);
  const inputPlaceholderColor = getTextColorWithOpacity(inputBackgroundColor, 0.5);
  const inputIconColor = getTextColorWithOpacity(inputBackgroundColor, 0.7);

  // Detect if we're in builder preview mode
  const isInBuilderPreview = chatWindowId === "builder-customize-preview" || (typeof window !== "undefined" && !!document.querySelector("[data-builder-preview]"));

  // Stable callbacks for UltraNavigationBar to reduce re-renders during streaming/typing
  const handleStop = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);
  const handleRetry = useCallback(() => {
    sendMessageRef.current(lastFailedUserMessageRef.current, true);
  }, []);
  const handleSend = useCallback(async () => {
    const messageContent = input.trim();
    const messageToSend = messageContent || (attachments.length > 0 ? "What's in the attached image(s)?" : "");
    if (!messageToSend || loading) return;
    setInput("");
    setAttachments([]);
    await sendMessageRef.current(messageToSend);
  }, [input, attachments, loading]);
  const handleResponseModeChange = useCallback((mode: ResponseMode) => {
    setResponseMode(mode);
    if (mode === "smart" && input.trim()) {
      const detected = detectBestMode(input);
      if (detected !== "smart") setResponseMode(detected);
    }
  }, [input]);

  // Render complete interface
  return (
    <div 
      className={`flex ${isInBuilderPreview ? "h-full w-full" : "h-screen"} relative overflow-hidden`}
      style={{
        // Animated gradient that shifts and moves - alive and stable
        background: `linear-gradient(135deg, ${gradientColors.primary} 0%, ${gradientColors.secondary} 25%, ${gradientColors.primary} 50%, ${gradientColors.secondary} 75%, ${gradientColors.primary} 100%)`,
        backgroundSize: "400% 400%",
        animation: "gradientShift 15s ease infinite",
        minHeight: isInBuilderPreview ? "100%" : '-webkit-fill-available', // iOS Safari fix
        height: isInBuilderPreview ? "100%" : undefined,
        width: isInBuilderPreview ? "100%" : undefined,
        position: isInBuilderPreview ? "relative" : undefined,
      }}
    >
      <BrandStyles brand={profile.brand} />
      
      {/* Clarity & Outcome Tracking */}
      <ClarityTracker
        profile={profile}
        userId={profile.ownerId}
        messages={messages
          .filter(m => m.role === "user" || m.role === "assistant")
          .map(m => ({
            role: m.role as "user" | "assistant",
            content: m.content,
          }))}
      />
      
      {/* Sidebar - Can be toggled on desktop and mobile */}
      {!hideSidebar && (
        <ChatSidebar
          profile={profile}
          isOpen={sidebarOpen} // Controlled by state - can be hidden on desktop too
          onClose={() => setSidebarOpen(false)}
          hasMultiChat={hasMultiChat}
          onNewChat={() => {
            // Create a new chat session
            const newChatId = crypto.randomUUID();
            router.push(`/chat?aiId=${profile.id}&chatId=${newChatId}`);
          }}
          onSelectAI={(aiId) => {
            router.push(`/chat?aiId=${aiId}`);
          }}
          onSelectTeamChannel={(teamId, channelId) => {
            router.push(`/team?teamId=${teamId}&channelId=${channelId}`);
          }}
        />
      )}

      {/* Main Chat Area - Simple structure like templates */}
      <div 
        className={`flex flex-col flex-1 min-w-0 relative pt-safe ${isInBuilderPreview ? "h-full" : "h-screen"}`}
        style={{
          minHeight: isInBuilderPreview ? 0 : undefined,
          height: isInBuilderPreview ? "100%" : undefined,
        }}
      >
        {/* Customizable Chat Interface Ring - DISABLED per user request */}
        {/* {profile.brand?.chatRingConfig?.enabled !== false && (
          <div className="absolute inset-0 pointer-events-none z-[1]">
            <RingSystem 
              type="user" 
              color={profile.brand.chatRingConfig?.color || profile.brand.primaryColor}
              customConfig={{
                opacity: profile.brand.chatRingConfig?.opacity ?? 0.25,
                blur: profile.brand.chatRingConfig?.blur ?? 50,
                width: profile.brand.chatRingConfig?.width ?? 4,
              }}
            />
          </div>
        )} */}
        {/* Enhanced animated background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div 
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"
            style={{ backgroundColor: gradientColors.primary }}
          />
          <div 
            className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"
            style={{ backgroundColor: gradientColors.secondary }}
          />
          {/* Subtle overlay for depth */}
          <div 
            className="absolute inset-0"
            style={{
              background: `radial-gradient(circle at 30% 50%, ${gradientColors.primary}15 0%, transparent 50%), radial-gradient(circle at 70% 50%, ${gradientColors.secondary}15 0%, transparent 50%)`
            }}
          />
        </div>

        {/* Header */}
        <div className="relative z-10 flex-shrink-0 border-b backdrop-blur-xl" style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}>
          <div
            className="absolute inset-0 premium-white-glass opacity-90"
            style={{
              background: `linear-gradient(135deg, ${profile.brand.primaryColor}08 0%, ${profile.brand.secondaryColor}08 100%)`,
            }}
          />
          <div className="relative" style={{ maxWidth: '48rem', margin: '0 auto', padding: '1rem' }}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {!hideSidebar && (
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 rounded-lg hover:bg-black/10 text-black/90 transition flex-shrink-0 z-20 relative"
                    aria-label="Toggle sidebar"
                    type="button"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                )}
                <div className="min-w-0 flex-1 flex items-center gap-3">
                  {profile.brand?.logoUrl && (
                    <img
                      src={profile.brand.logoUrl}
                      alt={profile.name}
                      className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                      style={{
                        border: `2px solid ${profile.brand.primaryColor}40`,
                      }}
                    />
                  )}
                <div className="min-w-0 flex-1">
                  <h1 
                    className="text-xl font-bold truncate"
                    style={{ color: profile.brand.textColor || "#000000" }}
                  >
                    {profile.name}
                  </h1>
                  <p 
                    className="text-sm capitalize truncate"
                    style={{ color: profile.brand.textSecondaryColor || "rgba(0, 0, 0, 0.7)" }}
                  >
                    {profile.modePreset} mode
                  </p>
                  </div>
                </div>
              </div>
              {/* Multi-Chat button moved to ChatSidebar */}
            </div>
          </div>
        </div>

        {/* Messages Container - Simple like templates */}
        <div 
          className="flex-1 overflow-y-auto relative z-10 overscroll-contain"
          style={{ paddingBottom: '140px', paddingRight: '4px' }} // Increased padding to prevent ultra bar overlap
          role="log"
          aria-live="polite"
          aria-atomic="false"
          aria-label="Chat messages"
        >
          <div style={{ maxWidth: '48rem', margin: '0 auto', padding: '0 1rem', paddingBottom: '1rem' }}>
            {messages.length === 0 ? (
              <div className="flex items-center justify-center min-h-[60vh] px-4">
                <div className="text-center max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="premium-white-glass rounded-3xl p-8 md:p-10 mb-6" style={{ 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
                }}>
                    <h2 
                      className="text-2xl md:text-3xl font-semibold mb-3 leading-tight"
                      style={{ color: inputTextColor }}
                    >
                      {getEmptyStateMessage(profile, karmaProfile).title}
                    </h2>
                    <p 
                      className="text-base md:text-lg leading-relaxed"
                      style={{ color: inputPlaceholderColor }}
                    >
                      {getEmptyStateMessage(profile, karmaProfile).subtitle}
                  </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-6 md:py-8">
                {messages.map((msg, index) => (
                  <div key={msg.id}>
                    <div
                      className="message-enter"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <ChatMessageComponent
                        message={msg}
                        profile={profile}
                        isStreaming={typingMessage?.id === msg.id}
                        previousMessage={index > 0 ? messages[index - 1] : null}
                        isError={(msg as ChatMessage & { isError?: boolean }).isError}
                        onRetry={(msg as ChatMessage & { isError?: boolean }).isError ? handleRetry : undefined}
                      />
                    </div>
                  </div>
                ))}
                
                {/* Typing message (streaming) – wrapper has ref when overlay is shown for collapse-on-stream-end */}
                {typingMessage && (
                  <div
                    ref={loading || collapseInProgress ? streamingBubbleRef : undefined}
                    className={`flex justify-start mt-6 ${loading || collapseInProgress ? "relative" : ""}`}
                  >
                    <div className="relative max-w-[85%] md:max-w-[75%] lg:max-w-[65%]">
                      <ChatMessageComponent
                        message={{
                          id: typingMessage.id,
                          role: "assistant",
                          content: typingMessage.content,
                        }}
                        profile={profile}
                        isStreaming={true}
                        previousMessage={messages[messages.length - 1]}
                      />
                      {showGlyphShimmer && (
                        <div
                          className="absolute inset-0 rounded-2xl pointer-events-none"
                          style={{
                            background: `linear-gradient(105deg, transparent 0%, ${profile.brand?.primaryColor || "#6366f1"}18 50%, transparent 100%)`,
                            animation: "atlas-glyph-shimmer 0.16s ease-out forwards",
                          }}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Atlas Constellation Synthesis – pending bubble (before first token); first-paint CSS glow */}
                {loading && !typingMessage && (
                  <div className="flex justify-start mt-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div
                      ref={pendingBubbleRef}
                      className="relative rounded-2xl min-h-[80px] flex items-center justify-center atlas-pending-bubble-glow"
                      style={{
                        maxWidth: '75%',
                        minWidth: 200,
                        borderRadius: assistantBubbleRadius,
                        padding: bubblePadding,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        boxShadow: `0 2px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9), 0 0 24px ${(profile.brand?.primaryColor || "#6366f1")}25`,
                        border: `1px solid ${(profile.brand?.primaryColor || "#6366f1")}30`,
                      }}
                      aria-busy="true"
                      aria-label="Atlas is thinking"
                    />
                  </div>
                )}

                {/* Overlay: shown for full loading (including while streaming) and until collapse finishes; anchorRef for reuse */}
                {(loading || collapseInProgress) && (
                  <AtlasProcessingOverlay
                    pending={loading}
                    anchorRef={typingMessage ? streamingBubbleRef : pendingBubbleRef}
                    ghostStreamItems={ghostStreamItems}
                    primaryColor={profile.brand?.primaryColor || "#6366f1"}
                    secondaryColor={profile.brand?.secondaryColor || "#8b5cf6"}
                    messageLength={lastSentMessageLengthRef.current}
                    onCollapseDone={() => setCollapseInProgress(false)}
                  />
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>
      </div>

      <UltraNavigationBar
        profile={profile}
        input={input}
        onInputChange={setInput}
        onSend={handleSend}
        onStop={handleStop}
        loading={loading}
        responseMode={responseMode}
        onResponseModeChange={handleResponseModeChange}
        karmaProfile={karmaProfile}
        atlasProcess={atlasProcess}
        atlasNavigationResult={atlasNavigationResult}
        atlasHistory={atlasHistory}
        isVoiceActive={false}
        isBuilderMode={false}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
      />
    </div>
  );
}
